import { priceOnFinish } from '../../supplementary_components/priceInputDelegate';
import { API_ROOT } from '../../supplementary_components/requests';
import { addNotification } from '../../supplementary_components/store';
import {
    applyPurposeFieldVisibility,
} from './issueMoneyCodeForm';
import {
    applyEquipmentTypeFieldVisibility,
    invoiceEquipmentTypeOnChange,
    TRAILER_FIELD,
    trailerSuggestionsLink,
    TRUCK_FIELD,
    validateInvoiceEquipment,
} from './payableInvoiceFormFields';

const EDIT_FORM_NAME = 'editMoneyCodeForm';
const TRUCK_PURPOSES = new Set(['Fuel', 'Scale', 'Accessories']);
const COMMON_MONEY_CODE_PURPOSES = new Set([
    'CashAdvance',
    'Fuel',
    'Scale',
    'LumperFee',
    'Accessories',
    'Parking',
    'Other',
]);

export const EDIT_MONEY_CODE_PURPOSE_OPTIONS = [
    { value: '', label: '' },
    { value: 'ExistingInvoice', label: 'Existing Invoice' },
    { value: 'CashAdvance', label: 'Cash Advance' },
    { value: 'Fuel', label: 'Fuel' },
    { value: 'Scale', label: 'Scale' },
    { value: 'LumperFee', label: 'Lumper Fee' },
    { value: 'Accessories', label: 'Accessories' },
    { value: 'Parking', label: 'Parking' },
    { value: 'Other', label: 'Other' },
];

function purposeToFormSelection(purpose) {
    if (purpose === 'Invoice') return 'ExistingInvoice';
    return purpose ?? '';
}

function purposeSelectionOnChange(event) {
    let formTarget = event.target;
    while (formTarget && formTarget.tagName !== 'FORM') {
        formTarget = formTarget.parentNode;
    }
    if (!formTarget) return;
    applyPurposeFieldVisibility(formTarget, event.target.value);
}

function buildEditMoneyCodePayload(data) {
    const purposeSelection = data.purposeSelection;
    const payload = {};

    if (purposeSelection === 'ExistingInvoice') {
        payload.purpose = 'Invoice';
        payload.invoice = data.invoice;
    } else {
        payload.purpose = purposeSelection;
    }

    if (data.driver) {
        payload.driver = data.driver;
    }
    if (purposeSelection === 'Parking') {
        if (data.equipmentType === 'Truck' && data.invoiceTruck) {
            payload.truck = data.invoiceTruck;
        } else if (data.equipmentType === 'Trailer' && data.invoiceTrailer) {
            payload.trailer = data.invoiceTrailer;
        }
    } else if (data.truck) {
        payload.truck = data.truck;
    }
    if (data.load) {
        payload.load = data.load;
    }
    if (data.reimbursed) {
        payload.reimbursed = data.reimbursed;
    }
    if (data.description !== undefined) {
        payload.description = data.description || null;
    }

    return payload;
}

async function validateEditMoneyCodeForm(data) {
    const purpose = data.purposeSelection;
    if (!purpose) {
        addNotification({ message: 'Select a purpose', type: 'alert' });
        return { cancelSubmission: true };
    }
    if (purpose === 'ExistingInvoice') {
        if (!data.invoice) {
            addNotification({ message: 'Select an invoice', type: 'alert' });
            return { cancelSubmission: true };
        }
        return {};
    }
    if (purpose === 'CashAdvance') {
        if (!data.driver) {
            addNotification({ message: 'Select a driver', type: 'alert' });
            return { cancelSubmission: true };
        }
        if (!data.reimbursed) {
            addNotification({ message: 'Select reimbursement status', type: 'alert' });
            return { cancelSubmission: true };
        }
        return {};
    }
    if (TRUCK_PURPOSES.has(purpose) && !data.truck) {
        addNotification({ message: 'Select a truck', type: 'alert' });
        return { cancelSubmission: true };
    }
    if (purpose === 'Parking') {
        const equipmentResult = await validateInvoiceEquipment({
            equipmentType: data.equipmentType,
            truck: data.invoiceTruck,
            trailer: data.invoiceTrailer,
        });
        if (equipmentResult.cancelSubmission) {
            return equipmentResult;
        }
    }
    if (purpose === 'LumperFee') {
        if (!data.load) {
            addNotification({ message: 'Select a load', type: 'alert' });
            return { cancelSubmission: true };
        }
        if (!data.reimbursed) {
            addNotification({ message: 'Select reimbursement status', type: 'alert' });
            return { cancelSubmission: true };
        }
        return {};
    }
    if (purpose === 'Other' && (!data.description || !String(data.description).trim())) {
        addNotification({ message: 'Description is required', type: 'alert' });
        return { cancelSubmission: true };
    }
    return {};
}

export function buildEditMoneyCodeForm(moneyCode) {
    const initialPurpose = purposeToFormSelection(moneyCode?.purpose);
    const showDriver = initialPurpose === 'CashAdvance';
    const showLoad = initialPurpose === 'LumperFee';
    const showTruck = TRUCK_PURPOSES.has(initialPurpose);
    const showParking = initialPurpose === 'Parking';
    const parkingEquipmentType = moneyCode?.trailer ? 'Trailer' : 'Truck';
    const showInvoice = initialPurpose === 'ExistingInvoice';
    const showReimbursed = showDriver || showLoad;
    const showDescription = COMMON_MONEY_CODE_PURPOSES.has(initialPurpose);

    return {
        formTitle: 'Edit Money Code',
        customSubmitButton: 'Save',
        name: EDIT_FORM_NAME,
        customMethod: 'PUT',
        addLink: API_ROOT + `moneycodes/${moneyCode.id}`,
        successMessage: 'Money code was updated',
        EZCloseAllowed: false,
        buildRequestBody: buildEditMoneyCodePayload,
        customCheckBeforeSending: validateEditMoneyCodeForm,
        customUseEffect: () => {
            const form = document.getElementById(EDIT_FORM_NAME);
            if (form) {
                applyPurposeFieldVisibility(form, initialPurpose);
                if (showParking) {
                    applyEquipmentTypeFieldVisibility(form, parkingEquipmentType, {
                        truckField: 'invoiceTruck',
                        trailerField: 'invoiceTrailer',
                    });
                }
            }
        },
        columns: [
            {
                label: 'Purpose',
                name: 'purposeSelection',
                type: 'select',
                dataset: EDIT_MONEY_CODE_PURPOSE_OPTIONS,
                mandatory: true,
                defaultValue: () => initialPurpose,
                onChange: purposeSelectionOnChange,
            },
            {
                label: 'Amount',
                name: 'amountDisplay',
                defaultValue: () => (moneyCode?.amount != null ? priceOnFinish(moneyCode.amount) : '-'),
                disabled: true,
                ignoreAsRegularField: true,
            },
            {
                label: 'Driver',
                name: 'driver',
                suggestionsEnabled: true,
                suggestionsLink: API_ROOT + 'drivers/namecontaines?statusagnostic=true&name=',
                dataValueRequired: true,
                mandatory: true,
                autoComplete: 'off',
                customDisplayMode: showDriver ? 'block' : 'none',
                disabled: !showDriver,
                defaultValue: () =>
                    [moneyCode?.driver?.firstName, moneyCode?.driver?.lastName].filter(Boolean).join(' ').trim(),
                predefinedDatValue: moneyCode?.driver ? JSON.stringify(moneyCode.driver) : null,
                displayValueGenerator: (rawValue) =>
                    [rawValue.firstName, rawValue.lastName].filter(Boolean).join(' ').trim(),
                afterMinimumChars: 2,
            },
            {
                label: 'Load',
                name: 'load',
                suggestionsEnabled: true,
                suggestionsLink: API_ROOT + 'loads/findLoadByLoadNumber?loadNumber=',
                dataValueRequired: true,
                mandatory: true,
                autoComplete: 'off',
                customDisplayMode: showLoad ? 'block' : 'none',
                disabled: !showLoad,
                defaultValue: () => moneyCode?.load?.loadNumber ?? '',
                predefinedDatValue: moneyCode?.load ? JSON.stringify(moneyCode.load) : null,
                displayValueGenerator: (val) =>
                    `${val.loadNumber}${val.broker?.brokerageName ? ` (${val.broker.brokerageName})` : ''}`,
                afterMinimumChars: 2,
            },
            {
                label: 'Invoice #',
                name: 'invoice',
                suggestionsEnabled: true,
                suggestionsLink: API_ROOT + 'invoices/search?pattern=',
                dataValueRequired: true,
                mandatory: true,
                autoComplete: 'off',
                customDisplayMode: showInvoice ? 'block' : 'none',
                disabled: !showInvoice,
                defaultValue: () =>
                    moneyCode?.invoice?.invoiceNumber != null ? String(moneyCode.invoice.invoiceNumber) : '',
                predefinedDatValue: moneyCode?.invoice ? JSON.stringify(moneyCode.invoice) : null,
                displayValueGenerator: (rawValue) =>
                    rawValue.invoiceNumber != null ? String(rawValue.invoiceNumber) : '',
                rawValComparationFieldName: 'invoiceNumber',
                afterMinimumChars: 1,
            },
            {
                label: 'Equipment type',
                name: 'equipmentType',
                type: 'select',
                dataset: ['', 'Truck', 'Trailer'],
                mandatory: true,
                customDisplayMode: showParking ? 'block' : 'none',
                disabled: !showParking,
                defaultValue: () => parkingEquipmentType,
                onChange: invoiceEquipmentTypeOnChange,
            },
            {
                ...TRUCK_FIELD,
                label: 'Truck',
                name: 'invoiceTruck',
                mandatory: true,
                customDisplayMode: showParking && parkingEquipmentType === 'Truck' ? 'block' : 'none',
                disabled: !showParking,
                defaultValue: () => moneyCode?.truck?.truckNumber ?? '',
                predefinedDatValue: moneyCode?.truck ? JSON.stringify(moneyCode.truck) : null,
            },
            {
                ...TRAILER_FIELD,
                name: 'invoiceTrailer',
                suggestionsLink: trailerSuggestionsLink(false),
                mandatory: true,
                customDisplayMode: showParking && parkingEquipmentType === 'Trailer' ? 'block' : 'none',
                disabled: !showParking,
                defaultValue: () => moneyCode?.trailer?.trailerNumber ?? '',
                predefinedDatValue: moneyCode?.trailer ? JSON.stringify(moneyCode.trailer) : null,
            },
            {
                label: 'Truck',
                name: 'truck',
                suggestionsEnabled: true,
                suggestionsLink: API_ROOT + 'trucks/allTrucksBySimilarTruckNumber?pattern=',
                dataValueRequired: true,
                mandatory: true,
                autoComplete: 'off',
                customDisplayMode: showTruck ? 'block' : 'none',
                disabled: !showTruck,
                defaultValue: () => moneyCode?.truck?.truckNumber ?? '',
                predefinedDatValue: moneyCode?.truck ? JSON.stringify(moneyCode.truck) : null,
                displayValueGenerator: (rawValue) => `${rawValue.truckNumber}`,
                rawValComparationFieldName: 'truckNumber',
                afterMinimumChars: 2,
            },
            {
                label: 'Reimbursement',
                name: 'reimbursed',
                type: 'select',
                dataset: ['', 'NotReimbursed', 'Reimbursed'],
                mandatory: true,
                customDisplayMode: showReimbursed ? 'block' : 'none',
                disabled: !showReimbursed,
                defaultValue: () => moneyCode?.reimbursed ?? '',
            },
            {
                label: 'Description',
                name: 'description',
                type: 'textArea',
                rowCount: 4,
                customDisplayMode: showDescription ? 'block' : 'none',
                disabled: !showDescription,
                defaultValue: () => moneyCode?.description ?? '',
            },
        ],
    };
}
